<template>
  <el-drawer v-model="drawerVisible" size="480px" title="智能硬件设备管理">
    <template #header>
      <h2 class="drawer__title">智能硬件设备管理</h2>
    </template>
    <div v-if="deviceTabs.length" class="drawer__tabs">
      <button
        v-for="tab in deviceTabs"
        :key="tab.value"
        class="drawer__tab"
        :class="{ 'drawer__tab--active': activeTab === tab.value }"
        type="button"
        @click="handleTabChange(tab.value)"
      >
        <span class="drawer__tab-text">{{ tab.label }}</span>
        <span class="drawer__tab-line"></span>
      </button>
    </div>
    <div v-if="!isBluetoothBadge" class="condition">
      <button
        v-for="tab in bindTypeTabs"
        :key="tab.value"
        class="condition__tag"
        :class="{ 'condition__tag--active': bindType === tab.value }"
        type="button"
        @click="handleBindTypeChange(tab.value)"
      >
        {{ tab.label }}
      </button>
      <button class="condition__add-btn" type="button" @click="openAddDeviceDrawer">
        <i class="condition__add-icon icon-add-line t-iconfont"></i>
        <span>添加设备</span>
      </button>
    </div>
    <div class="search">
      <el-input
        v-model="keyword"
        class="search__input"
        clearable
        placeholder="搜索设备 ID、员工"
        @clear="handleKeywordInput"
        @input="handleKeywordInput"
      >
        <template #prefix>
          <i class="icon-search-line search__icon t-iconfont"></i>
        </template>
      </el-input>
    </div>
    <!-- 抽屉主体 -->
    <main v-loading="drawerLoading" class="drawer__main">
      <template v-if="drawerContentList?.length">
        <article v-for="item in drawerContentList" :key="item.id" class="drawer__device-card">
          <header class="drawer__device-card-header">
            <img class="drawer__device-card-icon" :src="item.icon" />
            <div class="drawer__device-card-info">
              <div class="drawer__device-card-top">
                <h3 class="drawer__device-id">
                  设备 ID：<span v-html="highlightText(item?.deviceNo)"></span>
                </h3>
                <div class="drawer__device-status">
                  <img
                    class="drawer__device-status-icon"
                    :src="require('@/assets/battery-low-line.png')"
                  />
                  <span class="drawer__device-status-percent">{{ item.power }}%</span>
                </div>
              </div>
              <p class="drawer__device-capacity">
                剩余容量：
                <span class="drawer__device-capacity-value">{{ item.storageSize }}KB</span>
              </p>
            </div>
          </header>
          <footer class="drawer__device-card-footer">
            <div class="drawer__device-employee">
              绑定员工：
              <span
                class="drawer__device-employee-bind"
                :class="{ 'drawer__device-employee-unbind': !item.bind }"
                v-html="highlightText(setBindingStatusStr(item).bindingName)"
              ></span>
            </div>
            <!-- 智能贴片，绑定，不展示 -->
            <el-button
              v-if="isBluetoothBadge ? item.bind : true"
              class="drawer__device-unbind-btn"
              :class="{ 'drawer__device-bind-btn': item.bind }"
              link
              plain
              @click="transferDialog(item)"
              >{{ setBindingStatusStr(item).bingingStatus }}</el-button
            >
          </footer>
        </article>
        <div class="drawer__load-more">
          <el-button
            v-if="badgeHasMore"
            :loading="loadMoreLoading"
            plain
            size="small"
            type="text"
            @click="loadMore"
          >
            点击加载更多
          </el-button>
          <span v-else class="not-more">已加载全部</span>
        </div>
      </template>
      <div v-else class="drawer__device-empty">
        <list-empty v-if="keyword" main-title="未找到相关搜索结果" title="请尝试修改搜索词" />
        <list-empty v-else title="暂无数据" />
      </div>
    </main>
  </el-drawer>
  <!-- 解绑对话框 -->
  <Dialog v-model:show="unbindDialogVisible" align-center style="width: 400px" title="解绑">
    <span class="text-main">确定解绑吗？解绑后，当前员工将无法查看该设备新产生的录音数据</span>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="closeUnbindDialog"> 取消 </el-button>
        <el-button type="primary" @click="unbindTheBadgeInfo"> 确定 </el-button>
      </div>
    </template>
  </Dialog>
  <AddDeviceDrawer
    v-model:visible="addDeviceDrawerVisible"
    :active-tab="activeTab"
    :device-tabs="deviceTabs"
  />
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, provide, watch } from 'vue'
import { getBadgeList, bindBadgeInfo, unbindBadgeInfo } from '@/api'
import { getBluetoothBadgeManageList, unbindBluetoothBadgeByAdmin } from '@/api/nveasy-bt'
import ListEmpty from '@/pages/ai-board/components/list-empty.vue'
import AddDeviceDrawer from './add-device-drawer.vue'
import Dialog from '@/components/ai-dialog/index.vue'
import { ElMessage } from 'element-plus'
import xbbSdk from 'xbb-sdk'

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  deciceTab: {
    type: Array,
    required: true,
    default: () => []
  }
})

const emit = defineEmits(['update:visible'])

const drawerVisible = computed({
  get() {
    return props.visible
  },
  set(value) {
    emit('update:visible', value)
  }
})
// 分页状态
const badgePage = ref(1)
const badgePageSize = ref(20)
const badgeHasMore = ref(true)
const drawerLoading = ref(false)
const loadMoreLoading = ref(false)
// 抽屉中显示的设备列表列表
const drawerContentList = ref([])
// 解绑对话框显示状态
const unbindDialogVisible = ref(false)
// 添加设备抽屉显示状态
const addDeviceDrawerVisible = ref(false)

// 当前选中的设备信息
const currentSelectDeviceInfo = ref(null)
const activeTab = ref('')
const requestSerial = ref(0)

// 搜索关键字
const keyword = ref('')

// 绑定状态筛选：undefined=全部 1=已绑定 0=未绑定
const bindType = ref(undefined)
const bindTypeTabs = [
  { label: '全部', value: undefined },
  { label: '已绑定', value: 1 },
  { label: '未绑定', value: 0 }
]

const handleBindTypeChange = async (value) => {
  if (bindType.value === value) return
  bindType.value = value
  keyword.value = ''
  resetListState()
  if (!activeTab.value) return
  await getTheBadgeList()
}

const openAddDeviceDrawer = () => {
  addDeviceDrawerVisible.value = true
}

let keywordTimer = null
const handleKeywordInput = () => {
  clearTimeout(keywordTimer)
  keywordTimer = setTimeout(async () => {
    resetListState()
    if (!activeTab.value) return
    await getTheBadgeList()
  }, 300)
}

const tabLabelMap = {
  badge: '智能胸牌',
  listing: '智能挂牌',
  bluetoothBadge: '智能贴片'
}

const deviceTabs = computed(() => {
  const tabSet = new Set(props.deciceTab || [])

  return ['badge', 'listing', 'bluetoothBadge']
    .filter((key) => tabSet.has(key))
    .map((key) => ({
      label: tabLabelMap[key],
      value: key
    }))
})

// 当前tab状态，是否为【智能贴片】
const isBluetoothBadge = computed(() => activeTab.value === 'bluetoothBadge')

const normalizeBadgeList = (list, isBluetooth) => {
  if (!isBluetooth) return list
  const normalizeBluetoothBadgeItem = (item) => ({
    ...item,
    bind: item?.bound,
    power: item?.batteryPercent ?? 0,
    icon: item?.icon || 'https://xbongbong.oss-cn-hangzhou.aliyuncs.com/cdnfile/jczx/jczx-1.svg',
    // storageSize: item.storageFreeKb ? Math.floor(Number(item.storageFreeKb) / 1024) : 0
    storageSize: item.storageFreeKb || 0
  })

  return list.map((item) => normalizeBluetoothBadgeItem(item))
}

const syncActiveTab = () => {
  const tabValues = deviceTabs.value.map((item) => item.value)

  if (!tabValues.length) {
    activeTab.value = ''
    return
  }

  if (!tabValues.includes(activeTab.value)) {
    activeTab.value = tabValues[0]
  }
}

const resetListState = () => {
  requestSerial.value += 1
  badgePage.value = 1
  badgeHasMore.value = true
  drawerContentList.value = []
  drawerLoading.value = false
  loadMoreLoading.value = false
}

const refreshDeviceList = async () => {
  syncActiveTab()
  resetListState()

  if (!activeTab.value) return

  await getTheBadgeList()
}

provide('refreshDeviceList', refreshDeviceList)

const handleTabChange = async (tabValue) => {
  if (tabValue === activeTab.value) return

  activeTab.value = tabValue
  keyword.value = ''
  bindType.value = undefined
  await refreshDeviceList()
}

/**
 * 加载更多（分页）
 */
const loadMore = async () => {
  if (!badgeHasMore.value || loadMoreLoading.value) return
  const nextPage = badgePage.value + 1
  await getTheBadgeList(nextPage, true)
}

/**
 * 获取设备列表
 * @param alias 类型别名 badge 工牌 listing 挂牌 bluetoothBadge 蓝牙贴片
 */
const getTheBadgeList = async (page = 1, append = false) => {
  const typeMap = {
    badge: 1,
    listing: 2
  }
  const requestId = requestSerial.value
  const currentType = typeMap[activeTab.value]

  if (!currentType && !isBluetoothBadge.value) return

  if (!append) {
    drawerLoading.value = true
    badgePage.value = page
    badgeHasMore.value = true
  } else {
    loadMoreLoading.value = true
  }

  try {
    const params = {
      page,
      pageSize: badgePageSize.value
    }
    if (!isBluetoothBadge.value) {
      params.type = currentType
    }
    if (bindType.value !== undefined) {
      params.bindType = bindType.value
    }
    if (keyword.value) {
      params.keyword = keyword.value
    }
    const listRequest = isBluetoothBadge.value ? getBluetoothBadgeManageList : getBadgeList
    const { result } = await listRequest(params)

    if (requestId === requestSerial.value) {
      const list = normalizeBadgeList(result?.list || [], isBluetoothBadge.value)

      if (append) {
        drawerContentList.value = [...drawerContentList.value, ...list]
        badgePage.value = page
      } else {
        drawerContentList.value = list
        badgePage.value = page
      }

      badgeHasMore.value = list.length === badgePageSize.value
    }
  } catch (error) {
    if (requestId === requestSerial.value) {
      console.error('获取工牌列表失败:', error)
    }
  } finally {
    if (requestId === requestSerial.value) {
      if (!append) drawerLoading.value = false
      else loadMoreLoading.value = false
    }
  }
}

/**
 * 打开绑定成员对话框
 */
const openPersonSelectDialog = () => {
  xbbSdk.sendMessage('personSelect', {
    title: '绑定成员',
    activeTabName: 'user',
    showTabsName: ['user'],
    multiple: {
      user: false
    },
    otherParams: {
      parentStrictly: true,
      notEmptySubmit: true
    }
  })
}

/**
 * 打开解绑对话框
 */
const openUnbindDialog = () => {
  unbindDialogVisible.value = true
}

/**
 * 关闭解绑对话框
 */
const closeUnbindDialog = () => {
  unbindDialogVisible.value = false
}

/**
 * 解绑
 */
const unbindTheBadgeInfo = async () => {
  closeUnbindDialog()
  drawerLoading.value = true

  try {
    // 调用解绑接口
    if (isBluetoothBadge.value) {
      await unbindBluetoothBadgeByAdmin({
        deviceNo: currentSelectDeviceInfo.value.deviceNo
      })
    } else {
      await unbindBadgeInfo({
        dataId: currentSelectDeviceInfo.value.id
      })
    }
    // 解绑成功提示
    ElMessage({
      message: '解绑成功',
      type: 'success',
      plain: true
    })
    // 重新获取工牌列表
    await getTheBadgeList()
  } catch (error) {
    console.error('工牌解绑失败:', error)
  } finally {
    drawerLoading.value = false
  }
}

/**
 * 对话框打开中转
 * @param {Object} deviceInfo - 设备信息
 */
const transferDialog = (deviceInfo) => {
  if (typeof deviceInfo !== 'object' || deviceInfo === null) {
    console.error('入参非法，须为对象')
    return
  }
  // 更新当前选中设备信息
  currentSelectDeviceInfo.value = deviceInfo

  // 根据 deviceInfo 的 bind 属性决定打开哪个对话框
  deviceInfo.bind ? openUnbindDialog() : openPersonSelectDialog()
}

/**
 * 设置绑定状态文字
 * @param {Object} data - 设备绑定信息对象，包含 bind 和 userName 属性
 * @returns {Object} - 包含绑定员工名称和绑定状态文字的对象
 */
/**
 * 高亮文本中匹配 keyword 的部分
 * @param {string} text - 原始文本
 * @returns {string} - 包含高亮 span 的 HTML 字符串
 */
const highlightText = (text) => {
  if (!text) return text ?? ''
  const kw = keyword.value?.trim()
  if (!kw) return text
  const escaped = kw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const reg = new RegExp(`(${escaped})`, 'gi')
  return String(text).replace(reg, '<span style="color:#7a52f4;font-weight:600;">$1</span>')
}

const setBindingStatusStr = (data) => {
  // 默认的绑定信息
  const bindingInfo = {
    bindingName: '未绑定',
    bingingStatus: '绑定'
  }

  // 若设备已绑定，更新绑定信息
  if (data?.bind) {
    bindingInfo.bindingName = data?.userName || '未知员工'
    bindingInfo.bingingStatus = '解绑'
  }

  return bindingInfo
}

/**
 * 绑定
 * @param data 设备信息
 */
const bindTheBadgeInfo = async (data) => {
  drawerLoading.value = true

  try {
    const res = await bindBadgeInfo({
      platform: 'web',
      dataId: currentSelectDeviceInfo.value.id,
      bindUserId: data?.id
    })
    if (res?.success) {
      ElMessage({
        message: '绑定成功',
        type: 'success',
        plain: true
      })
    }
    // 重新获取工牌列表
    await getTheBadgeList()
  } catch (err) {
    console.error('工牌绑定失败:', err)
  } finally {
    drawerLoading.value = false
  }
}

/**
 * 从 xbb 获取消息
 * @param event 消息事件对象
 */
const getMessageFormXbb = (event) => {
  const { data, type } = event.data

  if (type === 'personSelectResult') {
    data?.length && bindTheBadgeInfo(data[0])
  }
}

// 是否已添加过监听器
const isMessageListenerAdded = ref(false)

watch(
  () => props.deciceTab,
  async () => {
    await refreshDeviceList()
  },
  {
    immediate: true,
    deep: true
  }
)

onMounted(() => {
  if (!isMessageListenerAdded.value) {
    window.addEventListener('message', getMessageFormXbb)
    isMessageListenerAdded.value = true
  }
})

onBeforeUnmount(() => {
  if (isMessageListenerAdded.value) {
    window.removeEventListener('message', getMessageFormXbb)
    isMessageListenerAdded.value = false
  }
})
</script>

<style lang="scss" scoped>
.drawer {
  &__title {
    font-family: PingFang SC;
    font-size: 16px;
    font-weight: 600;
    color: #323233;
  }

  &__tabs {
    display: flex;
    align-items: center;
    padding: 16px;
    box-sizing: border-box;
  }

  &__tab {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 68px;
    height: 27px;
    padding: 0 12px 0 0;
    margin-right: 12px;
    border: none;
    border-radius: 64px;
    background: transparent;
    cursor: pointer;
    box-sizing: border-box;

    &:last-child {
      margin-right: 0;
    }

    &-text {
      font-family: PingFang SC;
      font-size: 14px;
      font-weight: 400;
      color: #646566;
      line-height: 20px;
    }

    &-line {
      width: 20px;
      height: 2px;
      margin-top: 4px;
      border-radius: 4px;
      background: transparent;
    }

    &--active {
      .drawer__tab-text {
        font-weight: 600;
        color: #323233;
      }

      .drawer__tab-line {
        height: 3px;
        background: #7a52f4;
      }
    }
  }

  &__main {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
    padding: 16px;
  }

  &__device-card {
    width: 100%;
    border-radius: 12px;
    box-sizing: border-box;
    border: 1px solid #e5e6eb;
  }

  &__device-card-header {
    display: flex;
    align-items: center;
    padding: 12px;
  }

  &__device-card-icon {
    margin-right: 12px;
  }

  &__device-card-info {
    flex: 1;
  }

  &__device-card-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__device-id {
    font-family: PingFang SC;
    font-size: 14px;
    font-weight: 600;
    color: #323233;
  }

  &__device-status {
    display: flex;
    align-items: center;
  }

  &__device-status-icon {
    margin-right: 4px;
  }

  &__device-status-percent {
    font-family: PingFang SC;
    font-size: 14px;
    color: #323233;
  }

  &__device-capacity {
    font-family: PingFang SC;
    font-size: 14px;
    color: #646566;

    &-value {
      color: #323233;
    }
  }

  &__device-card-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    border-top: 1px solid #e5e6eb;
  }

  &__device-employee {
    display: flex;
    align-items: center;
    font-family: PingFang SC;
    font-size: 14px;
    color: #646566;
    flex: 1;
    overflow: hidden;

    &-bind {
      display: inline-block;
      color: #323233;
      // 超出长度省略
      // width: 300px;
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &-unbind {
      color: #c8c9cc;
    }
  }

  &__device-unbind-btn {
    font-family: PingFang SC;
    font-size: 14px;
    font-weight: normal;
    color: #248df5;
  }
  &__device-bind-btn {
    color: #969799;
  }

  &__device-empty {
    display: flex;
    align-items: center;
    height: 60vh;
    font-family: PingFang SC;
    font-size: 14px;
    color: #646566;
  }

  &__load-more {
    width: 100%;
    display: flex;
    justify-content: center;
    .not-more {
      color: #969799;
      font-size: 12px;
    }
    :deep(.el-button) {
      .el-button--text:not(.is-disabled):hover {
        color: unset;
      }
    }
  }
}

.search {
  padding: 12px 16px 8px;

  &__input {
    width: 100%;
    height: 36px;
    border-radius: 24px;
    background: #f2f3f5;

    :deep(.el-input__wrapper) {
      border-radius: 24px;
      background: #f2f3f5;
      box-shadow: none;
      padding: 0 12px;
    }

    :deep(.el-input__inner) {
      font-family: PingFang SC;
      font-size: 14px;
      color: #323233;
      background: transparent;

      &::placeholder {
        /* 文字/Text-2 次要信息 */
        color: #969799;
      }
    }

    :deep(.el-input__prefix) {
      margin-right: 4px;
    }
  }

  &__icon {
    font-size: 16px;
    /* 文字/Text-2 次要信息 */
    color: #969799;
  }
}

.condition {
  display: flex;
  align-items: center;
  padding: 0 16px;

  &__tag {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    padding: 0 12px;
    margin-right: 8px;
    border: 1px solid #f2f3f5;
    border-radius: 8px;
    background: transparent;
    font-family: PingFang SC;
    font-size: 14px;
    color: #646566;
    cursor: pointer;
    box-sizing: border-box;

    &:last-child {
      margin-right: 0;
    }

    &--active {
      background: #f5e8ff;
      border-color: #f5e8ff;
      color: #7a52f4;
    }
  }

  &__add-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 96px;
    height: 28px;
    margin-left: auto;
    padding: 0 8px;
    border: none;
    border-radius: 6px;
    background: #7a52f4;
    font-family: PingFang SC;
    font-size: 14px;
    line-height: 20px;
    color: #fff;
    cursor: pointer;
    box-sizing: border-box;
  }

  &__add-icon {
    margin-right: 8px;
    font-size: 16px;
    color: #fff;
  }
}
</style>
